// src/hooks/useAlertsApi.tsx

import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  ReactNode,
} from 'react'

import { authHeader, notifyError, A_MONTH_AGO } from '../api/'
import { usePreloader } from '../components/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

const AlertsCtx = createContext({ data: [], invalidate: () => {} })

type Props = {
  children: ReactNode
}

export const AlertsDataProvider = ({ children }: Props) => {
  const [data, setData] = useState([])

  const { getPool } = useAlertsApi({ cache: data })

  const invalidate = useCallback(() => {
    setData([])
  }, [])

  useEffect(() => {
    let active = true

    const fetchData = async () => {
      try {
        const result = await getPool()
        if (active && Array.isArray(result)) setData(result)
      } catch (error) {
        const statusCode = error?.status ?? error?.response?.status
        notifyError(`AlertsApi: GET messages ${statusCode}`)
      }
    }
    fetchData()

    return () => {
      active = false
    }
  }, [getPool])

  return (
    <AlertsCtx.Provider value={{ data, invalidate }}>
      {children}
    </AlertsCtx.Provider>
  )
}

export const useAlertsPool = () => useContext(AlertsCtx).data
export const useAlertsInvalidate = () => useContext(AlertsCtx).invalidate

type UseAlertsApiOverrides = {
  cache?: unknown[]
}

export const useAlertsApi = (overrides: UseAlertsApiOverrides = {}) => {
  const { start, stop } = usePreloader()
  const pool = useContext(AlertsCtx)

  const cacheRef = useRef(overrides.cache ?? pool?.data ?? [])
  useEffect(() => {
    cacheRef.current = overrides.cache ?? pool?.data ?? []
  }, [overrides.cache, pool?.data])

  const inflightRef = useRef(new Map())

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          throw {
            message:
              e2?.message ||
              (typeof e2 === 'string' ? e2 : 'Unbekannter Fehler'),
            status: e2?.status ?? e2?.response?.status ?? 401,
          }
        }
      }

      throw {
        message:
          error?.message ||
          (typeof error === 'string' ? error : 'Unbekannter Fehler'),
        status,
      }
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPool = useCallback(
    (opts: { force?: boolean } = {}) => {
      const force = opts?.force === true

      const cached = cacheRef.current
      if (!force && Array.isArray(cached) && cached.length > 0)
        return Promise.resolve(cached)

      const key = 'alerts:list'
      const inflight = inflightRef.current.get(key)
      if (inflight) return inflight

      const exec = () =>
        withRequest(async () => {
          const alertsMax = 5
          const mask = 7

          await refreshToken()

          const url = `${REACT_APP_API_URL}/messages/v1?page=1&limit=${alertsMax}&status=red&mask=${mask}`
          const options = { headers: { ...authHeader() }, method: 'GET' }
          const response = await fetch(url, options)

          if (response.status === 204) return []

          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }

          const payload = await response.json()
          const list = Array.isArray(payload?.data) ? payload.data : []

          return list.filter(entry => {
            const ts = entry?.attributes?.timestamp
            const d = ts ? new Date(ts) : null

            return (
              d instanceof Date &&
              !isNaN(d.getTime()) &&
              d.getTime() > A_MONTH_AGO.getTime()
            )
          })
        })

      const p = withPreloader(exec).finally(() => {
        inflightRef.current.delete(key)
      })

      inflightRef.current.set(key, p)
      return p
    },
    [withRequest, withPreloader]
  )

  return { getPool }
}
